import React from 'react';
import { createTheme, styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Switch, { switchClasses } from '@mui/material/Switch';
import {
	Avatar, Badge, Box, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, SvgIcon, Typography
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { MRT_EditActionButtons } from 'material-react-table';

const pxToRem = (px) => `${px / 17}rem`;

// Themes
export const createSmartFielderTheme = (mode = 'light') => {
	return createTheme({
		palette: {
			mode,
			...(mode === 'dark' && {
				background: { default: '#121212', paper: '#1e1e1e' }
			}),
			primary: { main: '#dc5c87' },
		},
		typography: {
			fontFamily: 'Lato, sans-serif',
			fontSize: 13,
		},
		components: {
			MuiTableCell: {
				styleOverrides: {
					root: {
						padding: '4px 8px',
						borderBottom: `1px solid rgba(224, 224, 224, 0.6)`,
					},
					head: {
						fontWeight: 600,
						fontSize: '0.73rem',
						lineHeight: 1.2,
						textAlign: 'center',
						fontFamily: 'Lato, sans-serif',
						py: '6px',
					},
					body: {
						fontSize: '0.72rem',
						lineHeight: 1.2,
						fontFamily: 'Lato, sans-serif',
						py: '6px',
					},
				},
			},
			MuiToolbar: {
				styleOverrides: {
					root: {
						minHeight: '40px !important',
						padding: '0 8px',
						display: 'flex',
						justifyContent: 'space-between',
						gap: '8px',
					},
				},
			},
			MuiTableContainer: {
				styleOverrides: {
					root: {
						maxHeight: 500,
						overflowY: 'auto',
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						color: '#dc5c87',
						'&.Mui-disabled': {
							color: 'rgba(0,0,0,0.26)',
						},
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						fontSize: '0.75rem',
					},
				},
			},
		},
	});
};
// Components
export const SmartSwitch = styled(Switch)(({ theme }) => {
	const size = pxToRem(20);
	const offset = pxToRem(7);
	const isDark = theme.palette.mode === 'dark';
	const trackColor = isDark ? theme.palette.grey[800] : theme.palette.grey[50];
	const borderColor = isDark ? theme.palette.grey[700] : theme.palette.grey[300];
	const thumbShadow = isDark
		? '0 2px 4px rgba(0,0,0,0.6)'
		: '0 3px 8px rgba(0,0,0,0.15), 0 1px 1px rgba(0,0,0,0.16), 0 3px 1px rgba(0,0,0,0.1)';
	return {
		width: pxToRem(50),
		height: pxToRem(20),
		[`& .${switchClasses.switchBase}`]: {
			left: `${offset} !important`,
			transitionDuration: '300ms',
			[`&.${switchClasses.checked}`]: {
				[`& + .${switchClasses.track}`]: {
					backgroundColor: '#dc5c87',
					opacity: 1,
					border: 'none',
				},
			},
		},
		[`& .${switchClasses.thumb}`]: {
			backgroundColor: theme.palette.common.white,
			width: size,
			height: size,
			boxShadow: thumbShadow,
		},
		[`& .${switchClasses.track}`]: {
			borderRadius: 40,
			border: `1px solid ${borderColor}`,
			backgroundColor: trackColor,
			opacity: 1,
			transition: theme.transitions.create(['background-color', 'border']),
		},
	};
});
export const SmartBadge = styled(Badge)({
	'& .MuiBadge-badge': {
		backgroundColor: '#dc5c87',
		color: '#fff',
	},
});
export const SmartIconButton = styled(IconButton)(({ theme }) => ({
	color: '#dc5c87',
}));
export const SmartDivider = styled(Divider)({
	marginLeft: '3px',
	marginRight: '3px',
	width: '2px',
	background: '#dc5c87',
});
export const SmartTaskCount = styled(Typography)({
	fontFamily: 'Lato, sans-serif',
	color: '#dc5c87',
});
/*export const compactHeadCellStyle = {
	fontSize: '0.73rem',
	py: '6px',
	fontWeight: 600,
	fontFamily: 'Lato, sans-serif',
	lineHeight: 1.2,
	textAlign: 'center',
};
export const compactBodyCellStyle = {
	fontSize: '0.72rem',
	py: '6px',
	fontFamily: 'Lato, sans-serif',
	lineHeight: 1.2,
};*/
// SmartToolbar styles
export const SmartToolbarContainerFull = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100%',
	flexWrap: 'wrap',
	gap: 1,
};
export const SmartToolbarContainerAuto = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: 'auto',
	flexWrap: 'wrap',
	gap: 1,
};
export const SmartToolbarGroupLeft = {
	display: 'flex',
	alignItems: 'center',
	gap: 1,
};
export const SmartToolbarGroupCenter = {
	display: 'flex',
	alignItems: 'center',
	gap: 1,
	flexWrap: 'wrap',
};
export const SmartToolbarGroupRight = {
	display: 'flex',
	alignItems: 'center',
	gap: 0,
};
export const SmartAlertBannerContainer = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	paddingLeft: 1,
	paddingBottom: 1,
};
export const SmartTableContainerSx = (height) => ({
	maxHeight: height || 500,
	overflowY: 'auto',
});
// Dialog styles
export const SmartDialogLayout = ({ title, table, row, children }) => {
	const theme = useTheme();
	const darkMode = theme.palette.mode === 'dark';
	return (
		<>
			<DialogTitle>
				<Typography variant="h6" textAlign="center">{title}</Typography>
			</DialogTitle>
			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem',
					backgroundColor: darkMode ? theme.palette.grey[900] : theme.palette.background.paper,
				}}
			>
				{children}
			</DialogContent>
			<DialogActions
				sx={{
					'& button:first-of-type': {
						color: '#dc5c87',
						border: '1px solid #dc5c87',
					},
					'& button:last-of-type': {
						backgroundColor: '#dc5c87',
						color: '#fff',
						'&:hover': { backgroundColor: '#c94e77' },
					},
				}}
			>
				<MRT_EditActionButtons variant="text" table={table} row={row} />
			</DialogActions>
		</>
	);
};
// Generic styles for SmartTable
export const SmartTypography = styled(Typography)({
	fontFamily: 'Lato, sans-serif',
});
export const SmartTitleTypography = styled(Typography)({
	fontFamily: 'Lato, sans-serif',
	mr: 2,
	display: { xs: 'none', md: 'flex' },
	fontWeight: 700,
	letterSpacing: '.3rem',
	color: 'inherit',
	textDecoration: 'none',
});
export const SmartAvatarTitle = styled(Avatar)(({ theme }) => ({
	display: 'flex',
	marginRight: theme.spacing(1),
	width: 32,
	height: 32,
	backgroundColor: 'transparent',

	'& img': {
		objectFit: 'contain'
	}
}));
export const SmartSvgTitle = styled('img')(({ theme }) => ({
	display: 'flex',
	marginRight: theme.spacing(2),
	width: 30,
	height: 30,
	objectFit: 'contain',
	backgroundColor: 'transparent',
}));
// Action Popup styles
export const SmartActionStack = styled(Stack)({
	mx: 0.75,
	minWidth: 40,
	textAlign: 'center',
});
export const SmartActionLabel = styled(Typography)({
	fontSize: 9,
	fontWeight: 500,
	color: 'text.secondary',
	lineHeight: 1.2,
	maxWidth: 65,
	pl: 1,
	pr: 1,
});
export const SmartCloseFloatingButton = styled(IconButton)(({ theme }) => ({
	position: 'absolute',
	top: -8,
	right: -8,
	width: 24,
	height: 24,
	backgroundColor: '#fff',
	border: '0px solid #dc5c87',
	zIndex: 10,
	boxShadow: theme.shadows[2],
	padding: 0,
	'&:hover': {
		backgroundColor: '#dc5c87',
	},
	'& svg': {
		color: '#dc5c87',
		'&:hover': {
			color: '#fff',
		},
	},
}));
export const SmartFloatingPopupContainer = styled(Box)({
	position: 'fixed',
	bottom: 45,
	left: '50%',
	transform: 'translateX(-50%)',
	zIndex: 1500,
});
export const SmartActionPaper = styled(Paper)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: theme.spacing(1, 2),
	backgroundColor: theme.palette.background.paper,
	position: 'relative',
}));
export const SmartActionAccentBar = styled(Box)({
	width: 3,
	height: 35,
	backgroundColor: '#f8445b',
	marginLeft: '-16px',
	borderTopLeftRadius: 8,
	borderBottomLeftRadius: 8,
});
